// F5: i18n チェック（ja/en キー同期・マスタ由来キー・生文字列翻訳型の未登録検出）
import { existsSync, readdirSync } from 'node:fs';
import path from 'node:path';
import { SEVENFOLD_DIR } from './repo-paths.mjs';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { readJsonIfExists, leafPaths, getByDotPath, walkJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';

const hasJapanese = (s) => /[　-ヿ㐀-鿿豈-﫿！-｠]/.test(s);

/**
 * i18n 検証結果の失敗内容を、取り込み・検証ジョブのログへそのまま流せる行配列にする。
 * 件数サマリだけでは対象文言が特定できず、失敗のたびに検証タブを開き直す必要があったため、
 * 未翻訳の実物（ファイル・種別・文言）をログ上で必ず見せる。
 */
export function formatI18nFindings(result, { maxLines = 30 } = {}) {
    const lines = [];
    for (const key of result?.messagesMissingInEn ?? []) lines.push(`  ✖ en.json messages に無いキー: ${key}`);
    for (const key of result?.messagesMissingInJa ?? []) lines.push(`  ✖ ja.json messages に無いキー: ${key}`);
    for (const m of result?.masterKeyMissing ?? []) {
        lines.push(`  ✖ マスタ由来キー欠落: ${m.file} ${m.jsonPath} → ${m.lang}.json messages.${m.key}`);
    }
    for (const x of result?.rawStringsUntranslated ?? []) {
        lines.push(`  ✖ 未翻訳（en.json exact 未登録）: ${x.file}（${x.kind}）「${x.text}」`);
    }
    if (lines.length > maxLines) {
        return [
            ...lines.slice(0, maxLines),
            `  … 他 ${lines.length - maxLines}件（検証タブの i18n チェックで全件を確認できます）`,
        ];
    }
    return lines;
}

export function runI18nCheck(gameId) {
    const dir = requireGameDir(gameId);
    const mapsDir = path.join(dir, 'maps');
    const localesDir = path.join(mapsDir, '_shared/i18n/locales');
    const ja = readJsonIfExists(path.join(localesDir, 'ja.json'));
    const en = readJsonIfExists(path.join(localesDir, 'en.json'));
    const result = {
        ok: true,
        messagesMissingInEn: [],
        messagesMissingInJa: [],
        sectionCounts: {},
        masterKeyMissing: [],
        rawStringsUntranslated: [],
        warnings: [],
    };
    if (!ja || !en) {
        result.ok = false;
        result.warnings.push('locales/ja.json または en.json が読めません。');
        return result;
    }

    // 1) messages のキー集合差分（ネストをドットパスで比較）
    // sevenfold 本体のロケールにも同じ非対称があるキーは「エンジン由来（既知）」として分離する
    // （例: equipment.nameAffixes は en 専用、王城 等の日本語名前空間は ja 専用）
    const jaKeys = new Set(leafPaths(ja.messages ?? {}));
    const enKeys = new Set(leafPaths(en.messages ?? {}));
    const sfJa = readJsonIfExists(path.join(SEVENFOLD_DIR, 'maps/_shared/i18n/locales/ja.json'));
    const sfEn = readJsonIfExists(path.join(SEVENFOLD_DIR, 'maps/_shared/i18n/locales/en.json'));
    const sfJaKeys = new Set(leafPaths(sfJa?.messages ?? {}));
    const sfEnKeys = new Set(leafPaths(sfEn?.messages ?? {}));
    const baselineMissingInEn = new Set([...sfJaKeys].filter((k) => !sfEnKeys.has(k)));
    const baselineMissingInJa = new Set([...sfEnKeys].filter((k) => !sfJaKeys.has(k)));
    const rawMissingInEn = [...jaKeys].filter((k) => !enKeys.has(k)).sort();
    const rawMissingInJa = [...enKeys].filter((k) => !jaKeys.has(k)).sort();
    result.messagesMissingInEn = rawMissingInEn.filter((k) => !baselineMissingInEn.has(k));
    result.messagesMissingInJa = rawMissingInJa.filter((k) => !baselineMissingInJa.has(k));
    result.inheritedAsymmetry = {
        missingInEn: rawMissingInEn.filter((k) => baselineMissingInEn.has(k)).length,
        missingInJa: rawMissingInJa.filter((k) => baselineMissingInJa.has(k)).length,
    };

    // 2) exact / patterns / fragments は役割が言語で異なるため件数を報告
    for (const section of ['exact', 'patterns', 'fragments']) {
        result.sectionCounts[section] = {
            ja: Object.keys(ja[section] ?? {}).length,
            en: Object.keys(en[section] ?? {}).length,
        };
    }

    // 3) マスタ JSON（items / skills / equipment）由来の必須キー欠落
    for (const name of ['items.json', 'skills.json', 'equipment.json']) {
        const json = readJsonIfExists(path.join(mapsDir, '_shared', name));
        if (!json) continue;
        walkJson(json, ({ key, value, path: p }) => {
            if ((key === 'nameKey' || key === 'descriptionKey') && typeof value === 'string') {
                for (const [lang, locale] of [['ja', ja], ['en', en]]) {
                    if (getByDotPath(locale.messages ?? {}, value) === undefined) {
                        result.masterKeyMissing.push({ file: `maps/_shared/${name}`, jsonPath: p, key: value, lang });
                    }
                }
            }
        });
    }

    // 4) 生文字列翻訳型（マップ名・エリア名・NPC 名・会話・通知・プロンプト・ワープ先名）の
    //    en.json exact 未登録を検出
    const enExact = en.exact ?? {};
    const seen = new Set();
    const pushRaw = (file, jsonPath, text, kind) => {
        if (typeof text !== 'string' || text.trim() === '' || !hasJapanese(text)) return;
        if (enExact[text] !== undefined) return;
        const dedupeKey = `${text}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        result.rawStringsUntranslated.push({ file, jsonPath, text, kind });
    };
    const mapDirs = readdirSync(mapsDir, { withFileTypes: true })
        .filter((e) => e.isDirectory() && e.name !== '_shared').map((e) => e.name);
    for (const mapId of mapDirs) {
        const mapDir = path.join(mapsDir, mapId);
        const meta = readJsonIfExists(path.join(mapDir, 'meta.json'));
        if (meta) {
            pushRaw(`maps/${mapId}/meta.json`, 'name', meta.name, 'マップ名');
            pushRaw(`maps/${mapId}/meta.json`, 'description', meta.description, 'マップ説明');
            pushRaw(`maps/${mapId}/meta.json`, 'defaultAreaName', meta.defaultAreaName, 'エリア名');
            for (const [i, a] of (meta.areas ?? []).entries()) {
                pushRaw(`maps/${mapId}/meta.json`, `areas[${i}].name`, a?.name, 'エリア名');
            }
        }
        const npcs = readJsonIfExists(path.join(mapDir, 'npcs.json'));
        if (npcs) {
            walkJson(npcs, ({ key, value, path: p }) => {
                if (key === 'name' && typeof value === 'string') pushRaw(`maps/${mapId}/npcs.json`, p, value, 'NPC 名');
                if (key === 'dialogues' && Array.isArray(value)) {
                    value.forEach((d, i) => pushRaw(`maps/${mapId}/npcs.json`, `${p}[${i}]`, d, '会話'));
                }
            });
        }
        // NPC AI人格チャットの固定セリフ（fallbackLines / defaultFallbackLines）は表示文言のため
        // en.json exact の対訳が必須（本家 Sevenfold npc-ai-chat 契約テストと同じ規約）。
        // persona 本文は LLM 入力専用のため対象外。
        const npcPersonas = readJsonIfExists(path.join(mapDir, 'npc-personas.json'));
        if (npcPersonas) {
            walkJson(npcPersonas, ({ key, value, path: p }) => {
                if ((key === 'fallbackLines' || key === 'defaultFallbackLines') && Array.isArray(value)) {
                    value.forEach((d, i) => pushRaw(`maps/${mapId}/npc-personas.json`, `${p}[${i}]`, d, 'AIチャット固定セリフ'));
                }
            });
        }
        const events = readJsonIfExists(path.join(mapDir, 'events.json'));
        if (events) {
            walkJson(events, ({ key, value, path: p }) => {
                if (key === 'message' && typeof value === 'string') pushRaw(`maps/${mapId}/events.json`, p, value, '通知文');
                if (key === 'prompt' && typeof value === 'string') pushRaw(`maps/${mapId}/events.json`, p, value, '調査プロンプト');
                if (key === 'destinationName' && typeof value === 'string') pushRaw(`maps/${mapId}/events.json`, p, value, 'ワープ先名');
            });
        }
        const objects = readJsonIfExists(path.join(mapDir, 'objects.json'));
        if (objects) {
            walkJson(objects, ({ key, value, path: p }) => {
                if ((key === 'prompt' || key === 'destinationName' || key === 'label') && typeof value === 'string') {
                    pushRaw(`maps/${mapId}/objects.json`, p, value, 'オブジェクト文言');
                }
            });
        }
    }

    result.ok = result.messagesMissingInEn.length === 0
        && result.messagesMissingInJa.length === 0
        && result.masterKeyMissing.length === 0
        && result.rawStringsUntranslated.length === 0;
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastValidation: { ...(meta.lastValidation ?? {}), i18n: { ok: result.ok, at: new Date().toISOString() } },
    }));
    auditLog('validate-i18n', {
        gameId,
        ok: result.ok,
        missingEn: result.messagesMissingInEn.length,
        missingJa: result.messagesMissingInJa.length,
        rawUntranslated: result.rawStringsUntranslated.length,
    });
    return result;
}
